import clamp from 'lodash/clamp'

export interface ZoneCardRect {
  x: number
  y: number
  width: number
  height: number
}

export interface ZoneCardViewport {
  width: number
  height: number
}

export type ZoneCardResizeCorner = 'top-left' | 'bottom-left' | 'bottom-right'

export function fitZoneCardRect(
  rect: ZoneCardRect,
  viewport: ZoneCardViewport
): ZoneCardRect {
  const width = Math.min(rect.width, Math.max(0, viewport.width))
  const height = Math.min(rect.height, Math.max(0, viewport.height))

  return {
    x: clamp(rect.x, 0, Math.max(0, viewport.width - width)),
    y: clamp(rect.y, 0, Math.max(0, viewport.height - height)),
    width,
    height,
  }
}

export function moveZoneCardRect(
  rect: ZoneCardRect,
  deltaX: number,
  deltaY: number,
  viewport: ZoneCardViewport
): ZoneCardRect {
  const fitted = fitZoneCardRect(rect, viewport)
  return {
    ...fitted,
    x: clamp(fitted.x + deltaX, 0, viewport.width - fitted.width),
    y: clamp(fitted.y + deltaY, 0, viewport.height - fitted.height),
  }
}

export function resizeZoneCardRect(
  start: ZoneCardRect,
  deltaX: number,
  deltaY: number,
  corner: ZoneCardResizeCorner,
  viewport: ZoneCardViewport,
  minWidth: number,
  minHeight: number
): ZoneCardRect {
  const rightEdge = start.x + start.width
  const bottomEdge = start.y + start.height
  const resizeFromLeft = corner === 'bottom-left' || corner === 'top-left'
  const resizeFromTop = corner === 'top-left'
  const maxWidth = Math.max(
    0,
    resizeFromLeft ? rightEdge : viewport.width - start.x
  )
  const maxHeight = Math.max(
    0,
    resizeFromTop ? bottomEdge : viewport.height - start.y
  )
  const effectiveMinWidth = Math.min(minWidth, start.width, maxWidth)
  const effectiveMinHeight = Math.min(minHeight, start.height, maxHeight)
  const width = clamp(
    start.width + (resizeFromLeft ? -deltaX : deltaX),
    effectiveMinWidth,
    maxWidth
  )
  const height = clamp(
    start.height + (resizeFromTop ? -deltaY : deltaY),
    effectiveMinHeight,
    maxHeight
  )

  return {
    x: resizeFromLeft ? rightEdge - width : start.x,
    y: resizeFromTop ? bottomEdge - height : start.y,
    width,
    height,
  }
}
